import { useMemo, useState } from 'react';
import { Pencil, MapPin, Users, Calendar } from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Table, type Column } from '@/shared/components/ui/Table';
import { useMasterDepartments } from '../api';
import type { EmploymentType, JobApplication, JobPosting, JobStatus } from '../types';

const STATUS_TONES: Record<JobStatus, BadgeTone> = { draft: 'neutral', open: 'success', closed: 'danger' };

const EMPLOYMENT_LABELS: Record<string, string> = {
  full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract', intern: 'Intern',
  FULL_TIME: 'Full Time', PART_TIME: 'Part Time', CONTRACTOR: 'Contract', INTERN: 'Intern', TEMPORARY: 'Temporary',
};

const EMPLOYMENT_OPTIONS: { label: string; value: EmploymentType }[] = [
  { label: 'Full Time', value: 'full_time' },
  { label: 'Part Time', value: 'part_time' },
  { label: 'Contract', value: 'contract' },
  { label: 'Intern', value: 'intern' },
];

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface JobPostingsTabProps {
  jobs: JobPosting[];
  applications: JobApplication[];
  canManage: boolean;
  onEdit: (job: JobPosting) => void;
  onToggleStatus: (job: JobPosting) => void;
  updateLoading: boolean;
}

export function JobPostingsTab({ jobs, applications, canManage, onEdit, onToggleStatus, updateLoading }: JobPostingsTabProps) {
  const [statusFilter, setStatusFilter] = useState<'all' | JobStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const appCountByJob = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const app of applications) {
      counts[app.jobId] = (counts[app.jobId] ?? 0) + 1;
    }
    return counts;
  }, [applications]);

  const filtered = useMemo(() => {
    let list = jobs;
    if (statusFilter !== 'all') list = list.filter((j) => j.status === statusFilter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((j) => j.title.toLowerCase().includes(term) || j.department.toLowerCase().includes(term) || j.location.toLowerCase().includes(term));
    }
    return list;
  }, [jobs, statusFilter, searchTerm]);

  const columns: Column<JobPosting>[] = [
    {
      key: 'title', header: 'Title',
      render: (j) => (
        <div>
          <p className="font-semibold text-on-surface">{j.title}</p>
          <p className="text-xs text-on-surface-variant">{j.department}</p>
        </div>
      ),
    },
    { key: 'location', header: 'Location', render: (j) => <span className="flex items-center gap-1 text-on-surface-variant"><MapPin className="h-3.5 w-3.5" />{j.location || '—'}</span> },
    { key: 'type', header: 'Type', render: (j) => <Badge tone="info" className="text-[10px]">{EMPLOYMENT_LABELS[j.employmentType] ?? j.employmentType}</Badge> },
    { key: 'applications', header: 'Applications', align: 'center', render: (j) => <span className="flex items-center justify-center gap-1"><Users className="h-3.5 w-3.5 text-on-surface-variant" />{appCountByJob[j._id] ?? 0}</span> },
    { key: 'openings', header: 'Openings', align: 'center', render: (j) => String(j.openings) },
    {
      key: 'status', header: 'Status',
      render: (j) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canManage || updateLoading}
            onClick={(e) => { e.stopPropagation(); onToggleStatus(j); }}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
              j.status === 'open' ? 'bg-success' : 'bg-surface-dim'
            } ${!canManage ? 'cursor-not-allowed opacity-50' : ''}`}
            aria-label={j.status === 'open' ? 'Deactivate' : 'Activate'}
            title={j.status === 'open' ? 'Click to close' : 'Click to open'}
          >
            <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${j.status === 'open' ? 'translate-x-4' : 'translate-x-0'}`} />
          </button>
          <Badge tone={STATUS_TONES[j.status]} className="text-[10px]">{j.status}</Badge>
        </div>
      ),
    },
    { key: 'created', header: 'Created', render: (j) => <span className="flex items-center gap-1 text-xs text-on-surface-variant"><Calendar className="h-3.5 w-3.5" />{fmtDate(j.createdAt)}</span> },
    {
      key: 'actions', header: 'Actions', align: 'right',
      render: (j) => canManage ? (
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(j); }}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Sub-filter row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[240px] flex-1">
          <Input
            placeholder="Search by title, department or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | JobStatus)}
            options={[
              { label: 'All statuses', value: 'all' },
              { label: 'Open', value: 'open' },
              { label: 'Closed', value: 'closed' },
              { label: 'Draft', value: 'draft' },
            ]}
          />
        </div>
      </div>

      <Card>
        <CardHeader title={`${filtered.length} job posting${filtered.length !== 1 ? 's' : ''}`} />
        <CardBody>
          {filtered.length === 0 ? (
            <EmptyState title="No job postings" description="Create a new job posting to get started." />
          ) : (
            <Table columns={columns} data={filtered} rowKey={(j) => j._id} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ─── Edit Job Modal ─────────────────────────────────────────────────────────────

interface EditJobModalProps {
  open: boolean;
  job: JobPosting;
  onClose: () => void;
  onSave: (id: string, payload: Partial<JobPosting> & { requiredSkills?: string[] }) => void;
  loading: boolean;
}

export function EditJobModal({ open, job, onClose, onSave, loading }: EditJobModalProps) {
  const [title, setTitle] = useState(job.title);
  const [department, setDepartment] = useState(job.department);
  const [description, setDescription] = useState(job.description);
  const [skills, setSkills] = useState(job.requiredSkills.join(', '));
  const [employmentType, setEmploymentType] = useState<EmploymentType>(job.employmentType as EmploymentType);
  const [location, setLocation] = useState(job.location);
  const [openings, setOpenings] = useState(String(job.openings));

  const { data: departments = [] } = useMasterDepartments();

  const handleSubmit = () => {
    if (!title.trim() || !department.trim()) return;
    onSave(job._id, {
      title: title.trim(),
      department: department.trim(),
      description: description.trim(),
      requiredSkills: skills.split(',').map((s) => s.trim()).filter(Boolean),
      employmentType,
      location: location.trim(),
      openings: Math.max(1, parseInt(openings, 10) || 1),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Job Posting"
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!title.trim() || !department.trim()}>Save Changes</Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <Input label="Job Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Select
          label="Department"
          options={departments.map((d) => ({ label: d.label, value: d.label }))}
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
        />
        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea
            className="h-24 w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <Input label="Required Skills (comma separated)" value={skills} onChange={(e) => setSkills(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Employment Type" options={EMPLOYMENT_OPTIONS} value={employmentType} onChange={(e) => setEmploymentType(e.target.value as EmploymentType)} />
          <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <Input label="Openings" type="number" value={openings} onChange={(e) => setOpenings(e.target.value)} />
      </div>
    </Modal>
  );
}
